import { SHIPPER_PORTAL_PREFIX } from 'constants/app.constants'
import { useTokenClaims } from 'hooks/useTokenClaims'
import { useQuery, UseQueryOptions } from 'react-query'
import { getCTSDK } from 'services/ctClient'
import { Doc } from 'types/doc.types'
import { Order } from 'types/order.types'
import { SearchAPIParams } from 'types/search.types'
import {
    Thread,
    ThreadCommChannelType,
    ThreadSearchFilters,
} from 'types/thread.types'
import { ThreadEvent } from 'types/threadEvent.types'
import { UserRole } from 'types/user.types'
import { QUERY_KEYS } from 'utils/queryKey.utils'

const sdk = getCTSDK()

const BASE_URL = '/threads'
type Model = Thread
const ELEMENT_NAME = 'thread'

const getUniversalBaseUrl = (role?: UserRole) => {
    return `${role === 'customerUser' ? SHIPPER_PORTAL_PREFIX : ''}${BASE_URL}`
}

export const useGetThreadWithExtraInfo = (
    id?: string,
    options?: Omit<UseQueryOptions<Model>, 'queryKey' | 'queryFn'>
) => {
    return useQuery(
        QUERY_KEYS.cartage.threads.useGetThreadWithExtraInfo(id),
        async () =>
            await sdk
                .get<{ [ELEMENT_NAME]: Model }>(`${BASE_URL}/${id}/extraInfo`)
                .then((res) => res.data[ELEMENT_NAME]),
        // @ts-ignore
        { enabled: !!id, ...options }
    )
}

export const useGetLinkedOrders = (
    id?: string,
    options?: Omit<UseQueryOptions<Order[]>, 'queryKey' | 'queryFn'>
) => {
    return useQuery(
        QUERY_KEYS.cartage.threads.useGetLinkedOrders(id),
        async () =>
            await sdk
                .get<{ orders: Order[] }>(`${BASE_URL}/${id}/orders`)
                .then((res) => res.data.orders),
        // @ts-ignore
        { enabled: !!id, ...options }
    )
}

export const useGetThreads = (
    params?: {
        orderId?: string
        commChannelType?: ThreadCommChannelType
        hasPartnerData?: boolean
    },
    options?: Omit<UseQueryOptions<Model[]>, 'queryKey' | 'queryFn'>
) => {
    return useQuery(
        QUERY_KEYS.cartage.threads.useGetThreads(params),
        async () =>
            await sdk
                .get<{ threads: Model[] }>(BASE_URL, { params })
                .then((res) => res.data.threads),
        options
    )
}

export const searchThreads = (params: SearchAPIParams<ThreadSearchFilters>) => {
    return sdk
        .post<{ total: number; result: Model[] }>(`${BASE_URL}/search`, {
            data: params,
        })
        .then((res) => res.data)
}

export const useSearchThreads = (
    params: SearchAPIParams<ThreadSearchFilters>,
    options?: Omit<
        UseQueryOptions<{ result: Model[]; total: number }>,
        'queryKey' | 'queryFn'
    >
) => {
    return useQuery(
        QUERY_KEYS.cartage.threads.useSearchThreads(params),
        async () => await searchThreads(params),
        // @ts-ignore
        options
    )
}

export const useGetThreadEventsById = (
    id?: string,
    options?: Omit<UseQueryOptions<ThreadEvent[]>, 'queryKey' | 'queryFn'>
) => {
    return useQuery(
        QUERY_KEYS.cartage.threads.useGetThreadEventsById(id),
        async () =>
            await sdk
                .get<{ events: ThreadEvent[] }>(`${BASE_URL}/${id}/events`)
                .then((res) => res.data.events),
        // @ts-ignore
        { enabled: !!id, ...options }
    )
}

export const useUniversalGetThreadEventsById = (
    id?: string,
    options?: Omit<UseQueryOptions<ThreadEvent[]>, 'queryKey' | 'queryFn'>
) => {
    const { role } = useTokenClaims()
    const url = `${getUniversalBaseUrl(role)}/${id}/events`
    return useQuery(
        QUERY_KEYS.cartage.threads.useUniversalGetThreadEventsById({
            id,
            role,
        }),
        async () =>
            await sdk
                .get<{ events: ThreadEvent[] }>(url)
                .then((res) => res.data.events),
        options
    )
}

export const useGetThreadDocumentsById = (
    args?: {
        threadId?: string
        hasOrderDocuments?: boolean
    },
    options?: Omit<UseQueryOptions<Doc[]>, 'queryKey' | 'queryFn'>
) => {
    const { threadId, ...params } = args ?? {}

    return useQuery(
        QUERY_KEYS.cartage.threads.useGetThreadDocumentsById(params),
        async () =>
            await sdk
                .get<{ documents: Doc[] }>(`${BASE_URL}/${threadId}/docs`, {
                    params,
                })
                .then((res) => res.data.documents),
        // @ts-ignore
        { enabled: !!threadId, ...options }
    )
}
